/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// React
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// @mui material components
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

// Data
import reportsBarChartData from "layouts/dashboard/data/reportsBarChartData";

// Dashboard components
import Projects from "layouts/dashboard/components/Projects";
import OrdersOverview from "layouts/dashboard/components/OrdersOverview";

// Custom components
import PlatformIcon from "components/PlatformIcon";
import VehicleCard from "components/VehicleCard";
import ContractInfoCard from "components/ContractInfoCard";
import FinancialInfoCard from "components/FinancialInfoCard";
import GlobalLevelCard from "components/GlobalLevelCard";
import PlatformRevenueChart from "components/PlatformRevenueChart";
import MobileBottomNav from "components/MobileBottomNav";

// Services
import { isAuthenticated, getUserData } from "services/authServiceAPI";
import { getVTCStats, getCachedStats } from "services/statsService";
import { getUserPreferences } from "services/userPreferencesService";

// Context
import {
  useMaterialUIController,
  setSidenavColor,
  setTransparentSidenav,
  setWhiteSidenav,
  setFixedNavbar,
  setDarkMode,
} from "context";

function Dashboard() {
  const navigate = useNavigate();
  const [controller, dispatch] = useMaterialUIController();
  const [stats, setStats] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [contractInfo, setContractInfo] = useState(null);
  const [niveauGlobal, setNiveauGlobal] = useState(0);
  const [previousWeekStats, setPreviousWeekStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [referent, setReferent] = useState(null);

  useEffect(() => {
    // Vérifier si l'utilisateur est authentifié
    if (!isAuthenticated()) {
      navigate("/authentication/sign-in");
      return;
    }

    // Récupérer les données utilisateur
    const userData = getUserData();
    if (!userData) {
      navigate("/authentication/sign-in");
      return;
    }

    setUser(userData);

    // Charger les préférences UI de l'utilisateur
    const loadPreferences = async () => {
      try {
        console.log('🎨 Chargement des préférences UI...');
        const preferences = await getUserPreferences(userData.email);

        if (preferences) {
          console.log('✅ Préférences UI chargées:', preferences);

          // Appliquer les préférences au contexte
          if (preferences.sidenavColor !== undefined) {
            setSidenavColor(dispatch, preferences.sidenavColor);
          }
          if (preferences.transparentSidenav !== undefined) {
            setTransparentSidenav(dispatch, preferences.transparentSidenav);
          }
          if (preferences.whiteSidenav !== undefined) {
            setWhiteSidenav(dispatch, preferences.whiteSidenav);
          }
          if (preferences.fixedNavbar !== undefined) {
            setFixedNavbar(dispatch, preferences.fixedNavbar);
          }
          if (preferences.darkMode !== undefined) {
            setDarkMode(dispatch, preferences.darkMode);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des préférences:', error);
      }
    };

    loadPreferences();

    // Fonction helper pour formater les montants
    const formatAmount = (value) => {
      if (value === null || value === undefined || value === 0) return "0,00 €";
      const num = typeof value === "string" ? parseFloat(value) : value;
      if (isNaN(num)) return "0,00 €";
      return num.toFixed(2).replace(".", ",") + " €";
    };

    // Récupérer les statistiques VTC
    const fetchStats = async () => {
      const startTime = Date.now(); // Mémoriser l'heure de début

      // 1. D'abord, charger les données en cache rapidement
      const cachedData = await getCachedStats(userData);
      if (cachedData) {
        console.log("📦 Affichage des données en cache");

        // Si statsFormatted existe, l'utiliser (avec pourcentages et labels), sinon formater avec valeurs par défaut
        const formattedCache = cachedData.statsFormatted || {
          uber: { count: formatAmount(cachedData.uber), percentage: "+0%", label: "Dernières données" },
          bolt: { count: formatAmount(cachedData.bolt), percentage: "+0%", label: "Dernières données" },
          heetch: { count: formatAmount(cachedData.heetch), percentage: "+0%", label: "Dernières données" },
          freenow: { count: formatAmount(cachedData.freenow), percentage: "+0%", label: "Dernières données" },
          espece: { count: formatAmount(cachedData.espece), percentage: "+0%", label: "Dernières données" },
        };

        setStats(formattedCache);
        setVehicle({
          marque: cachedData.marque || "",
          immat: cachedData.immat || "",
        });
        setNiveauGlobal(cachedData.niveauGlobal || 0);
        setContractInfo(cachedData.additional || null);
        setReferent(cachedData.additional?.referent || null);

        // Calculer le temps écoulé
        const elapsedTime = Date.now() - startTime;
        const minLoadingTime = 1000; // Minimum 1 seconde d'affichage

        // Si le chargement a été trop rapide, attendre pour une transition douce
        if (elapsedTime < minLoadingTime) {
          setTimeout(() => {
            // D'abord déclencher le fade out de l'écran de chargement
            setFadeOut(true);
            // Puis après l'animation de fade out (1000ms), afficher le dashboard
            setTimeout(() => {
              setInitialLoad(false);
              setLoading(false);
            }, 1000);
          }, minLoadingTime - elapsedTime);
        } else {
          setFadeOut(true);
          setTimeout(() => {
            setInitialLoad(false);
            setLoading(false);
          }, 1000);
        }
      }

      // 2. Ensuite, mettre à jour avec les données fraîches des webhooks
      const result = await getVTCStats(userData);
      if (result.success) {
        console.log("🔄 Mise à jour avec les données des webhooks");
        setStats(result.data);
        setVehicle(result.vehicle);
        setContractInfo(result.additional);
        setNiveauGlobal(result.niveauGlobal);
        setPreviousWeekStats(result.previousWeekStats);
        setReferent(result.additional?.referent || null);
      } else {
        // Si pas de données en cache et échec webhook, utiliser des données par défaut
        if (!cachedData) {
          setStats(result.data);
          setVehicle(null);
          setContractInfo(null);
          setNiveauGlobal(0);
          setPreviousWeekStats(null);
          setReferent(null);
        }
      }
      setInitialLoad(false);
      setLoading(false);
    };

    fetchStats();
  }, [navigate]);

  // Si initialLoad (premier chargement), afficher un écran de chargement plein écran SANS le layout
  if (initialLoad || loading) {
    return (
      <MDBox
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          backgroundColor: "#f0f2f5",
          animation: fadeOut ? "fadeOut 1s ease-out forwards" : "fadeIn 0.3s ease-in",
          "@keyframes fadeIn": {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          "@keyframes fadeOut": {
            from: { opacity: 1 },
            to: { opacity: 0 },
          },
        }}
      >
        <MDBox
          sx={{
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { transform: "scale(1)", opacity: 1 },
              "50%": { transform: "scale(1.05)", opacity: 0.8 },
            },
          }}
        >
          <CircularProgress size={60} thickness={4} />
        </MDBox>
        <MDTypography
          variant="h6"
          mt={3}
          color="text"
          sx={{
            animation: "fadeInUp 0.5s ease-out 0.2s both",
            "@keyframes fadeInUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          Chargement de vos données...
        </MDTypography>
      </MDBox>
    );
  }

  // Une fois chargé, afficher le dashboard complet avec animation
  return (
    <MDBox
      sx={{
        opacity: 0,
        animation: "fadeIn 0.5s ease-out forwards",
        animationDelay: "0.1s", // Petit délai pour laisser le DOM se construire
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <DashboardLayout>
        <DashboardNavbar contractInfo={contractInfo} />
        <MDBox py={3} px={{ xs: 2, sm: 3 }} pb={{ xs: 12, lg: 3 }}>
        <>
            {/* Niveau Global - Grande carte en haut */}
            <MDBox mb={3}>
              <GlobalLevelCard niveauGlobal={niveauGlobal} loading={loading} />
            </MDBox>

            {/* Cartes des plateformes */}
            <Grid container spacing={3}>
              <Grid item xs={6} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="dark"
                  icon={<PlatformIcon platform="uber" />}
                  title="UBER"
                  count={stats?.uber?.count || 0}
                  percentage={{
                    color: "success",
                    amount: stats?.uber?.percentage || "+0%",
                    label: stats?.uber?.label || "Non disponible",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="success"
                  icon={<PlatformIcon platform="bolt" />}
                  title="BOLT"
                  count={stats?.bolt?.count || 0}
                  percentage={{
                    color: "success",
                    amount: stats?.bolt?.percentage || "+0%",
                    label: stats?.bolt?.label || "Non disponible",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="primary"
                  icon={<PlatformIcon platform="heetch" />}
                  title="HEETCH"
                  count={stats?.heetch?.count || 0}
                  percentage={{
                    color: "success",
                    amount: stats?.heetch?.percentage || "+0%",
                    label: stats?.heetch?.label || "Non disponible",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="warning"
                  icon={<PlatformIcon platform="freenow" />}
                  title="FREENOW"
                  count={stats?.freenow?.count || 0}
                  percentage={{
                    color: "success",
                    amount: stats?.freenow?.percentage || "+0%",
                    label: stats?.freenow?.label || "Non disponible",
                  }}
                />
              </MDBox>
            </Grid>
            <Grid item xs={6} md={6} lg={2.4}>
              <MDBox mb={1.5}>
                <ComplexStatisticsCard
                  color="info"
                  icon="euro"
                  title="ESPECE"
                  count={stats?.espece?.count || "0,00 €"}
                  percentage={{
                    color: "success",
                    amount: stats?.espece?.percentage || "+0%",
                    label: stats?.espece?.label || "Non disponible",
                  }}
                />
              </MDBox>
            </Grid>
            </Grid>
          </>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <VehicleCard
                  marque={vehicle?.marque}
                  immat={vehicle?.immat}
                  loading={loading}
                />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <ContractInfoCard data={contractInfo} />
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <MDBox mb={3}>
                <FinancialInfoCard data={contractInfo} />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mt={4.5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={12}>
              <MDBox mb={3}>
                <PlatformRevenueChart
                  stats={stats}
                  previousWeekStats={previousWeekStats}
                  niveauGlobal={niveauGlobal}
                />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={8}>
              <Projects />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <OrdersOverview contractInfo={contractInfo} />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
      <MobileBottomNav vehicleData={vehicle} referent={referent} />
    </DashboardLayout>
    </MDBox>
  );
}

export default Dashboard;
